import { z } from 'zod';
import { Label, Project, ProjectColor, Task, TaskDetails } from './rows.js';

/** Team-plane contracts (architecture §9). These rows are fetched, not synced. */
export const Role = z.enum(['owner', 'admin', 'member']);
export type Role = z.infer<typeof Role>;
export const PlanStatus = z.enum(['trialing', 'active', 'past_due', 'paused', 'cancelled']);
export type PlanStatus = z.infer<typeof PlanStatus>;

export const TeamDto = z.object({
  id: z.string(), name: z.string(), ownerId: z.string(), planStatus: PlanStatus, trialEndsAt: z.string().nullable(), currentPeriodEnd: z.string().nullable(),
  seats: z.number().int(), autoJoinDomain: z.string().nullable(), domainVerifiedAt: z.string().nullable(), membersMayEditSessions: z.boolean(), createdAt: z.string(),
});
export type TeamDto = z.infer<typeof TeamDto>;

export const MemberDto = z.object({ userId: z.string(), name: z.string().nullable(), email: z.string(), role: Role, joinedAt: z.string() });
export type MemberDto = z.infer<typeof MemberDto>;

export const InviteDto = z.object({
  id: z.string(), kind: z.enum(['email', 'link', 'domain']), email: z.string().nullable(), role: z.enum(['admin', 'member']),
  expiresAt: z.string().nullable(), invitedBy: z.string(), invitedByName: z.string().nullable(), createdAt: z.string(), lastSentAt: z.string().nullable(),
});
export type InviteDto = z.infer<typeof InviteDto>;

/** Everything a client needs to render shared projects, locks and the seat line; polled with ETag (architecture §9). */
export const TeamState = z.object({
  team: TeamDto,
  myRole: Role,
  members: z.array(MemberDto),
  pendingInvites: z.number().int(),
  projects: z.array(Project),
  tasks: z.array(Task),
  labels: z.array(Label),
  serverTime: z.number().int(),
});
export type TeamState = z.infer<typeof TeamState>;

export const CreateTeamRequest = z.object({ name: z.string().min(1).max(80) });
export const UpdateTeamRequest = z.object({ name: z.string().min(1).max(80).optional(), membersMayEditSessions: z.boolean().optional() });
export const ChangeRoleRequest = z.object({ role: z.enum(['admin', 'member']) });
export const EmailInvitesRequest = z.object({ emails: z.array(z.string().email()).min(1).max(100), role: z.enum(['admin', 'member']).default('member') });
export const LinkInviteRequest = z.object({ expiresIn: z.enum(['24h', '7d', 'never']).default('7d') });
export const SetDomainRequest = z.object({ domain: z.string().min(3).max(253).regex(/^[a-z0-9.-]+\.[a-z]{2,}$/i) });
export const SeatPreviewRequest = z.object({ seatsToAdd: z.number().int().min(0).max(200) });
export const SeatPreview = z.object({ seatsNow: z.number().int(), seatsAfter: z.number().int(), chargeNowCents: z.number().int(), monthlyCents: z.number().int(), daysLeftInCycle: z.number().int(), currency: z.string() });
export type SeatPreview = z.infer<typeof SeatPreview>;

export const JoinInfo = z.object({ teamName: z.string(), inviterName: z.string().nullable(), role: z.enum(['admin', 'member']), expired: z.boolean(), alreadyMember: z.boolean() });
export type JoinInfo = z.infer<typeof JoinInfo>;

export const NewSharedProject = z.object({ name: z.string().min(1).max(80), color: ProjectColor, weeklyTargetSec: z.number().int().positive().nullable().default(null), labelIds: z.array(z.string()).default([]) });
export const ShareExistingProject = z.object({ projectId: z.string() });
export const UpdateSharedProject = z.object({ name: z.string().min(1).max(80).optional(), color: ProjectColor.optional(), weeklyTargetSec: z.number().int().positive().nullable().optional(), labelIds: z.array(z.string()).optional(), archived: z.boolean().optional() });
export const NewSharedTask = z.object({ name: z.string().min(1).max(200), estimateSec: z.number().int().positive().nullable().default(null), labelIds: z.array(z.string()).default([]), details: TaskDetails.prefault({}) });
export const UpdateSharedTask = z.object({ name: z.string().min(1).max(200).optional(), estimateSec: z.number().int().positive().nullable().optional(), labelIds: z.array(z.string()).optional(), details: TaskDetails.optional(), finished: z.boolean().optional(), position: z.number().int().optional() });
export const NewTeamLabel = z.object({ name: z.string().min(1).max(40), tint: ProjectColor.nullable().default(null) });
export const UpdateTeamLabel = z.object({ name: z.string().min(1).max(40).optional(), tint: ProjectColor.nullable().optional(), retired: z.boolean().optional() });

export const ReportScope = z.enum(['today', 'week', 'month']);
export const PersonReport = z.object({
  userId: z.string(), name: z.string().nullable(), role: Role, seconds: z.number().int(), pomodoros: z.number().int(), sessions: z.number().int(),
  /** Agent time this person handed off (§11.3). Never added to `seconds`. */
  agentSeconds: z.number().int().default(0),
  /** Whole cents, or null when no run reported any - which is not the same as nothing spent. */
  agentCostCents: z.number().int().nullable().default(null),
});
export const ProjectReport = z.object({
  projectId: z.string(), name: z.string(), color: ProjectColor, teamSeconds: z.number().int(), mySeconds: z.number().int(),
  agentSeconds: z.number().int().default(0),
  agentCostCents: z.number().int().nullable().default(null),
});
export const LabelReport = z.object({ labelId: z.string(), name: z.string(), seconds: z.number().int() });
export const ClaimedTask = z.object({ taskId: z.string(), name: z.string(), projectId: z.string(), projectName: z.string(), holderId: z.string(), holderName: z.string().nullable(), claimedAt: z.string(), trackedSec: z.number().int(), estimateSec: z.number().int().nullable() });
/** Admins get `people`; members get their own numbers and team totals only (spec §4.3). */
export const TeamReport = z.object({
  scope: ReportScope, from: z.string(), to: z.string(),
  totalSeconds: z.number().int(), totalPomodoros: z.number().int(),
  mySeconds: z.number().int(), myPomodoros: z.number().int(),
  people: z.array(PersonReport).nullable(),
  projects: z.array(ProjectReport),
  labels: z.array(LabelReport),
  claimed: z.array(ClaimedTask),
});
export type TeamReport = z.infer<typeof TeamReport>;

export const InvoiceDto = z.object({ id: z.string(), date: z.string(), description: z.string(), amountCents: z.number().int(), currency: z.string(), status: z.string(), pdfUrl: z.string().nullable() });
export const BillingSummary = z.object({
  planStatus: PlanStatus, seats: z.number().int(), seatPriceCents: z.number().int(), currency: z.string(),
  trialEndsAt: z.string().nullable(), renewsAt: z.string().nullable(), hasSubscription: z.boolean(),
  paymentMethod: z.object({ brand: z.string(), last4: z.string(), expMonth: z.number().int(), expYear: z.number().int() }).nullable(),
  nextInvoice: z.object({ amountCents: z.number().int(), taxCents: z.number().int(), dueAt: z.string().nullable() }).nullable(),
  invoices: z.array(InvoiceDto),
  billingDetails: z.object({ name: z.string().nullable(), email: z.string().nullable(), address: z.string().nullable() }).nullable(),
});
export type BillingSummary = z.infer<typeof BillingSummary>;
export const SetSeatsRequest = z.object({ seats: z.number().int().min(1).max(500) });
export const UrlResponse = z.object({ url: z.string() });
